import React, { useState, useEffect } from "react";

const IntroducerIncomeCalculator = ({ onClose }) => {
  const [plan, setPlan] = useState(20);
  const [maxLevel, setMaxLevel] = useState(8);
  const [refsPerLevel, setRefsPerLevel] = useState(5);
  const [amount, setAmount] = useState(20);
  const [errorMsg, setErrorMsg] = useState("");
  const [showEarningEstimator, setShowEarningEstimator] = useState(false);
  const [overallCommission, setOverallCommission] = useState(0);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!amount || isNaN(amount) || Number(amount) < 10 || Number(amount) > 99999) {
      setErrorMsg("Please enter a valid number between 10 to 99999 for the amount.");
      return;
    }

    if (!plan) {
      setErrorMsg("Please enter plan.");
      return;
    }

    const planNumber = Number(plan);
    if (planNumber !== 10 && planNumber !== 20) {
      setErrorMsg("Input 10 or 20 only in the plan");
      return;
    }

    setErrorMsg("");
    setShowEarningEstimator(true);
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const param2Number = Number(plan);
  let commissionRates = [0.005, 0.003, 0.002, 0.002, 0.002, 0.002, 0.002, 0.002];

  // Limit commissionRates array to maxLevel
  commissionRates = commissionRates.slice(0, maxLevel);

  const levels = Array.from({ length: maxLevel }, (_, i) => i + 1);

  const calculateCommission = (price, level) => {
    const rate = commissionRates[level - 1];
    const commission = price * rate * Math.pow(refsPerLevel, level);
    return commission.toFixed(2);
  };

  useEffect(() => {
    if (showEarningEstimator) {
      let totalCommission = 0;
      levels.forEach((level) => {
        const rate = commissionRates[level - 1];
        const commission = amount * rate * Math.pow(refsPerLevel, level);
        totalCommission += commission;
      });
      setOverallCommission(totalCommission.toFixed(2));
    }
  }, [amount, plan, maxLevel, refsPerLevel, showEarningEstimator]);

  const convertToIndianWords = (num) => {
    const a = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
      'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
    const b = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

    const getWords = (numStr) => {
      const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
        'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
      const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

      if (numStr.length === 1) return a[Number(numStr)];
      if (numStr.length === 2) {
        if (numStr[0] === '1') return a[Number(numStr)];
        return (b[Number(numStr[0])] + (numStr[1] !== '0' ? ' ' + a[Number(numStr[1])] : '')).trim();
      }
      return '';
    };

    const formattedNum = Math.floor(Number(num)).toString();
    if (!formattedNum || isNaN(formattedNum)) return 'Invalid number';
    if (formattedNum === '0') return 'Zero';
    if (formattedNum.length > 12) return 'number too large';

    let words = [];
    const length = formattedNum.length;

    if (length > 7) {
      const crorePart = formattedNum.slice(0, length - 7);
      if (crorePart) words.push(getWords(crorePart) + ' Crore');
    }
    if (length > 5) {
      const lakhPart = formattedNum.slice(length - 7, length - 5);
      if (lakhPart) words.push(getWords(lakhPart) + ' Lakh');
    }
    if (length > 3) {
      const thousandPart = formattedNum.slice(length - 5, length - 3);
      if (thousandPart) words.push(getWords(thousandPart) + ' Thousand');
    }
    if (length > 2) {
      const hundredPart = formattedNum.slice(length - 3, length - 2);
      if (hundredPart !== '0') {
        words.push(getWords(hundredPart) + ' hundred');
      }
    }

    const lastTwoDigits = formattedNum.slice(-2);
    if (lastTwoDigits) words.push(getWords(lastTwoDigits));

    return words.join(' ').trim();
  };

  return (
    <div className="w-full bg-gray-800 p-6 rounded-2xl shadow-md border border-pink-800 max-w-2xl mx-auto">
      <h4 className="text-lg font-semibold text-pink-200 text-center mb-4">
        Community Referral Income Estimator
      </h4>

      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <label htmlFor="amount" className="text-white font-medium">
            Price (Rate) / Spend:
          </label>
          <input
            type="number"
            id="amount"
            name="amount"
            value={amount}
            min={10}
            max={99999}
            step={1}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Enter amount"
            className={`w-28 h-10 px-3 py-2 border rounded-md text-center text-black focus:outline-none focus:ring-2 focus:ring-pink-500 ${
              errorMsg ? "border-red-500" : "border-pink-700"
            } bg-gray-700 text-white`}
          />
        </div>

        {/* Max Level */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <label htmlFor="maxLevel" className="text-white font-medium">
            Max Level:
          </label>
          <select
            id="maxLevel"
            value={maxLevel}
            onChange={(e) => setMaxLevel(e.target.value)}
            className="w-28 h-10 px-3 py-2 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-pink-500 bg-gray-700 border-pink-700"
          >
            {[...Array(8)].map((_, i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </select>
        </div>

        {/* References per Level */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <label htmlFor="refsPerLevel" className="text-white font-medium">
            References per Level:
          </label>
          <select
            id="refsPerLevel"
            value={refsPerLevel}
            onChange={(e) => setRefsPerLevel(e.target.value)}
            className="w-28 h-10 px-3 py-2 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-pink-500 bg-gray-700 border-pink-700"
          >
            {[...Array(9)].map((_, i) => (
              <option key={i + 2} value={i + 2}>
                {i + 2}
              </option>
            ))}
          </select>
        </div>

        {errorMsg && <p className="text-red-400 text-sm text-center">{errorMsg}</p>}

        {/* Buttons */}
        <div className="flex flex-row justify-center gap-3 mt-2">
          <button
            type="submit"
            className="w-40 bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white font-medium py-2 px-5 rounded-md transition duration-300"
          >
            💰 Estimate
          </button>

          <button
            type="button"
            onClick={handleClose}
            className="w-40 bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-5 rounded-md transition duration-300"
          >
            ❌ Close
          </button>
        </div>
      </form>

      {/* Estimated Income Table */}
      {showEarningEstimator && (
        <div className="mt-6">
          <p className="text-pink-200 text-sm sm:text-base mb-3 text-center">
            Estimated income table for ₹{amount}.00, with {refsPerLevel} referrals per level, introducer incentive plan up to {maxLevel} levels.
          </p>
          <div className="w-full p-4 rounded-lg shadow-lg overflow-x-auto">
            <table className="table-auto w-full text-white text-sm border border-pink-500">
              <thead>
                <tr>
                  <th className="px-2 py-2 text-left">Level : Rate</th>
                  <th className="px-2 py-2 text-left">Referrals : Incentive/Referral</th>
                  <th className="px-2 py-2 text-left">Total Earning (₹)</th>
                </tr>
              </thead>
              <tbody>
                {levels.map((level) => (
                  <tr key={level}>
                    <td className="border px-2 py-2 text-left">
                      {level} : {(commissionRates[level - 1] * 100).toFixed(2)}%
                    </td>
                    <td className="border px-2 py-2 text-left">
                      {refsPerLevel}<sup>{level}</sup> = {Math.pow(refsPerLevel, level)} : [<span className="text-yellow-200">{(amount * commissionRates[level - 1]).toFixed(2)}</span>]
                    </td>
                    <td className="border px-2 py-2 text-left">{calculateCommission(amount, level)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="text-yellow-200 text-sm mt-2 text-center">
              Total Estimated Incentive: ₹ {Number(overallCommission).toFixed(2).toLocaleString('en-IN')}
            </p>
            <p className="text-white text-sm text-center">
              In words: {convertToIndianWords(overallCommission)} Rupees
            </p>

            <p className="text-white mt-4 text-sm">
              N.B. -
              <ol className="list-decimal list-inside space-y-1">
                <li>Make Think5Whys Platform as main or additional source of income!!</li>
                <li>Earn PASSIVE INCOME - Up to level {maxLevel}</li>
                <li>Instant income for Direct Referrals as Active Income. Passive income for the current month will be transferred into your account within 10 working days next month.</li>
                <li>New Physical, Digital Products, Services, Subscriptions will be added regularly</li>
                <li>Estimated in ₹</li>
                <li>Price is may be INCLUSIVE or excluding 18% GST</li>
              </ol>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntroducerIncomeCalculator;